import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Package, Container as ContainerIcon, Wrench } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { InstallAppButton } from '../components/InstallAppButton';
import AuthShell, { REALM_THEME, type Realm } from '../components/AuthShell';
import { currentProduct } from '../lib/pageCatalog';

/**
 * Domain-driven login. Each product has its OWN login domain, so the visible
 * login is single-purpose (no toggle tabs) and the post-login view is scoped to
 * that product:
 *
 *   spares.crystalgrp.xyz  → Spares (parts & procurement). Lands on /.
 *   service.crystalgrp.xyz → Service (field service / job orders). Lands on /.
 *   ims.crystalgrp.xyz     → IMS Containers. Lands on /ims/overview.
 *
 * Spares vs Service share the same accounts + realm; they differ only in which
 * pages the SPA shows (page catalog `product` tag) and a per-account
 * productAccess gate the backend enforces at login. IMS is its own walled realm.
 *
 * On localhost / any un-mapped host `currentProduct()` returns null, so we fall
 * back to a 3-way tab picker for local dev (a `?product=` query also forces one).
 */
type LoginProduct = 'spares' | 'service' | 'containers';
const LAST_PRODUCT_KEY = 'ims_last_product';

function getLastProduct(): LoginProduct {
  try {
    const v = localStorage.getItem(LAST_PRODUCT_KEY);
    return v === 'containers' ? 'containers' : v === 'service' ? 'service' : 'spares';
  } catch { return 'spares'; }
}

const META: Record<LoginProduct, { title: string; sub: string; realm: Realm; highlights: string[] }> = {
  spares: {
    title: 'Spares IMS',
    sub: 'Crystal Logistic — AI-powered inventory',
    realm: 'spares',
    highlights: ['Requisition to PO to GRN, one thread', 'Live stock, reconciled to the yard', 'Approvals that reach the right person'],
  },
  service: {
    title: 'Service Desk',
    sub: 'Crystal Logistic — field service & job orders',
    realm: 'spares',
    highlights: ['Every job order, live from punch to close', 'Technicians, travel and on-site logs', 'Service reports and TAT analytics'],
  },
  containers: {
    title: 'IMS Containers',
    sub: 'Crystal Logistic — container yard analytics',
    realm: 'containers',
    highlights: ['Every container, where it is and what it is doing', 'Leases, returns and sales in one ledger', 'Stock health against your min/max plan'],
  },
};

// Cross-app login links — each product has its own domain, so switching apps is
// a full navigation to that domain's login page.
const PRODUCT_URL: Record<LoginProduct, string> = {
  spares: 'https://spares.crystalgrp.xyz',
  service: 'https://service.crystalgrp.xyz',
  containers: 'https://ims.crystalgroup.xyz',
};
const OTHER_META: Record<LoginProduct, { label: string; Icon: any }> = {
  spares: { label: 'Spares', Icon: Package },
  service: { label: 'Service', Icon: Wrench },
  containers: { label: 'IMS Containers', Icon: ContainerIcon },
};
const ALL_PRODUCTS: LoginProduct[] = ['spares', 'service', 'containers'];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  // Which product the domain fixes us to (null on localhost/dev → tab picker).
  const domain = currentProduct(); // 'spares' | 'service' | 'ims' | null
  const fixed: LoginProduct | null =
    domain === 'ims' ? 'containers' : domain === 'service' ? 'service' : domain === 'spares' ? 'spares' : null;
  const [product, setProduct] = useState<LoginProduct>(fixed ?? getLastProduct());
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const meta = META[product];
  const isContainers = product === 'containers';
  const t = REALM_THEME[meta.realm];

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const id = identifier.trim();
      // A purely numeric identifier is an EMPLOYEE CODE — send it verbatim.
      // IMS otherwise appends @ims.local to a bare id (the seeded "admin").
      const isEmployeeCode = /^[0-9]{3,10}$/.test(id);
      const emailToUse = isContainers && !isEmployeeCode ? (id.includes('@') ? id : `${id}@ims.local`) : id;
      await login(emailToUse, password, product);
      try { localStorage.setItem(LAST_PRODUCT_KEY, product); } catch { /* ignore */ }
      // Scope the post-login SPA view to the product tab that was used, so the
      // sidebar shows ONLY that product's pages even on localhost / unscoped
      // hosts. currentProduct() reads this cg-product key before falling back
      // to the hostname; on a real product subdomain it resolves the same value
      // from the host anyway, so this is a no-op there. 'containers' → 'ims'.
      try { sessionStorage.setItem('cg-product', product === 'containers' ? 'ims' : product); } catch { /* ignore */ }
      if (isContainers) {
        try { sessionStorage.setItem('ims_containers_pin', 'login-bypass'); } catch { /* ignore */ }
        navigate('/ims/overview');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      toast.error(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  function pickProduct(p: LoginProduct) {
    if (p === product) return;
    setProduct(p);
    setIdentifier('');
    setPassword('');
  }

  // Tabs only in dev / unscoped host — each real domain is single-purpose.
  const tabs = fixed === null ? (
    <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
      {([['spares', 'Spares', Package], ['service', 'Service', Wrench], ['containers', 'IMS', ContainerIcon]] as const).map(([p, label, Icon]) => (
        <button
          key={p}
          type="button"
          onClick={() => pickProduct(p)}
          className={`press flex items-center justify-center gap-1.5 rounded-md py-2 font-display text-sm font-bold transition ${
            product === p ? 'bg-white text-navy-600 shadow-1' : 'text-slate-500 hover:text-slate-950'
          }`}
          aria-pressed={product === p}
        >
          <Icon size={14} /> {label}
        </button>
      ))}
    </div>
  ) : undefined;

  // On a real product domain, offer a jump to the OTHER apps' login pages
  // (a full navigation, since each app is its own subdomain). Hidden in dev
  // where the tab picker already switches products in-place.
  const otherApps = fixed !== null ? ALL_PRODUCTS.filter((p) => p !== product) : [];

  return (
    <AuthShell realm={meta.realm} title={meta.title} tagline={meta.sub} highlights={meta.highlights} tabs={tabs} footer={<InstallAppButton />}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">{isContainers ? 'Employee code or ID' : 'Employee code or email'}</label>
          <input
            className={`input h-11 ${t.ring}`}
            type="text"
            required
            autoFocus
            autoComplete={isContainers ? 'username' : 'email'}
            placeholder={isContainers ? 'e.g. 9035, or admin' : 'e.g. 9035, or you@crystalgroup.in'}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Password</label>
          <input
            className={`input h-11 ${t.ring}`}
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <button
          className={`btn press h-11 w-full text-white shadow-card ${t.accent} ${t.accentHover}`}
          type="submit"
          disabled={busy}
        >
          {busy ? 'Signing in…' : `Sign in to ${meta.title}`}
        </button>
      </form>

      {otherApps.length > 0 && (
        <div className="mt-5 border-t border-slate-200 pt-4">
          <div className="mb-2 text-center text-xs text-slate-500">Looking for another app?</div>
          <div className="grid grid-cols-2 gap-2">
            {otherApps.map((p) => {
              const m = OTHER_META[p];
              return (
                <a
                  key={p}
                  href={PRODUCT_URL[p]}
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                >
                  <m.Icon size={14} /> {m.label}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </AuthShell>
  );
}
